import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Skeleton } from "../components/ui/skeleton";
import { Briefcase, MapPin, Search, Plus } from "lucide-react";

const FAMILIES = ["All", "CEO", "CTO", "CFO", "COO", "CMO", "CPO", "CRO", "Chief AI Officer", "Head of AI", "VP Engineering", "VP Product", "Head of Sales", "General Counsel"];

export default function Jobs() {
  const [q, setQ] = useState("");
  const [family, setFamily] = useState("All");
  const [roles, setRoles] = useState(null);

  useEffect(() => {
    const t = setTimeout(() => {
      const params = new URLSearchParams();
      if (q) params.set("q", q);
      if (family !== "All") params.set("role_family", family);
      api.get(`/roles?${params}`).then((r) => setRoles(r.data)).catch(() => setRoles([]));
    }, 250);
    return () => clearTimeout(t);
  }, [q, family]);

  return (
    <div className="space-y-8" data-testid="jobs-page">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
        <div>
          <div className="label-caps mb-2 flex items-center gap-1.5"><Briefcase className="h-3 w-3 text-amber-400" />Job board</div>
          <h1 className="text-4xl sm:text-5xl tracking-tight font-semibold">Seats worth taking.</h1>
          <p className="text-muted-foreground mt-3 max-w-2xl">Executive roles posted by organizations on the Tribunal. Every role comes with a library-grounded brief.</p>
        </div>
        <Button asChild className="rounded-full" data-testid="jobs-post-role-btn">
          <Link to="/app/orgs"><Plus className="h-4 w-4 mr-1" />Post a role</Link>
        </Button>
      </div>

      <div className="space-y-3">
        <div className="relative max-w-xl">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search title, company, location…" className="pl-9" data-testid="jobs-search" />
        </div>
        <div className="flex flex-wrap gap-1.5">
          {FAMILIES.map((f) => (
            <button key={f} onClick={() => setFamily(f)} data-testid={`jobs-family-${f.replace(/\s+/g, "-").toLowerCase()}`}
              className={`px-3 py-1 rounded-full text-xs border transition-colors duration-200 ${family === f ? "bg-primary text-primary-foreground border-primary" : "border-border text-muted-foreground hover:text-foreground"}`}>
              {f}
            </button>
          ))}
        </div>
      </div>

      {roles === null ? (
        <div className="space-y-3">{[0, 1, 2].map((i) => <Skeleton key={i} className="h-24" />)}</div>
      ) : roles.length === 0 ? (
        <Card className="p-10 text-center text-muted-foreground" data-testid="jobs-empty">No roles match yet. Create an organization and post the first one.</Card>
      ) : (
        <div className="grid md:grid-cols-2 gap-4" data-testid="jobs-list">
          {roles.map((r, i) => (
            <Link key={r.id} to={`/app/roles/${r.id}`} data-testid={`job-card-${r.id}`}
              className="animate-in fade-in slide-in-from-bottom-2 duration-500" style={{ animationDelay: `${i * 40}ms`, animationFillMode: "both" }}>
              <Card className="p-6 h-full hover:border-primary/50 hover:-translate-y-0.5 transition-[transform,border-color] duration-200">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="label-caps">{r.role_family}</div>
                    <div className="text-lg font-medium tracking-tight mt-1">{r.title}</div>
                    <div className="text-xs text-muted-foreground mt-2 flex flex-wrap items-center gap-2">
                      <span className="flex items-center gap-1"><Briefcase className="h-3 w-3" />{r.org_name}</span>
                      {r.location && <span className="flex items-center gap-1"><MapPin className="h-3 w-3" />{r.location}</span>}
                    </div>
                  </div>
                  {r.remote_policy && <Badge variant="outline" className="capitalize shrink-0">{r.remote_policy}</Badge>}
                </div>
                {(r.salary_min || r.salary_max) && (
                  <div className="font-mono text-xs text-muted-foreground mt-4">
                    ${(r.salary_min || 0).toLocaleString()} – ${(r.salary_max || 0).toLocaleString()}
                    {r.equity_max ? ` · up to ${r.equity_max}% equity` : ""}
                  </div>
                )}
              </Card>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
